import isEqual from "lodash.isequal";
import { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { cn } from "tailwind-variants";
import ArtworkAlert from "./ArtworkAlert.vue";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import ArtworkShippingProgress from "./ArtworkShippingProgress.vue";
import StepNumber from "./common/StepNumber.vue";
import { useDtfStore } from "./store";
import "./ArtworkQuantity.scss";

export default function ArtworkQuantity({
  index = 0,
  artwork = null,
  disabled = false,
  showFreeShipping = false,
  isMultiUpload = false,
  ...restProps
}) {
  const intl = useIntl();
  const store = useDtfStore();
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [previousData, setPreviousData] = useState({ quantity: null });

  function modalArtworks() {
    return store.modalArtworks;
  }

  function outOfStock() {
    return store.outOfStock;
  }

  function printAsIs() {
    return store.printAsIs;
  }

  function simulation() {
    return store.simulation;
  }

  function sticker() {
    return store.sticker;
  }

  function isPuff() {
    return store.isPuff;
  }

  function variantId() {
    return store.variantId;
  }

  function minStickerQuantity() {
    return store.minStickerQuantity;
  }

  function isV15() {
    return store.isV15;
  }

  function isMobile() {
    return store.isMobile;
  }

  function isJerseyNumbers() {
    return store.isJerseyNumbers;
  }

  function isLetters() {
    return store.isLetters;
  }

  function letterNumberArtwork() {
    return store.letterNumberArtwork;
  }

  function summary() {
    return store.summary;
  }

  function showStepsUI() {
    return store.showStepsUI;
  }

  function quantity() {
    if (isMobile()) {
      return artwork?.quantity || 0;
    }
    return artwork?.quantity;
  }

  function pricing() {
    if (isJerseyNumbers() || isLetters()) {
      return letterNumberArtwork();
    }
    if (artwork?.isFile) {
      return simulation();
    }
    return artwork?.pricing || simulation();
  }

  function percentage() {
    if (pricing()?.percentage) {
      return Number(pricing()?.percentage).toFixed(0);
    }
    return null;
  }

  function retailPrice() {
    if (pricing()?.retailPrice) {
      return Number(pricing().retailPrice).toFixed(2);
    }
    return null;
  }

  function unitPrice() {
    if (pricing()?.unitPrice) {
      return Number(pricing().unitPrice).toFixed(2);
    }
    return null;
  }

  function id() {
    return `artwork[${index}][variants][${variantId()}][quantity]`;
  }

  function retailTotal() {
    return (retailPrice() * quantity()).toFixed(2);
  }

  function total() {
    const value = unitPrice() * quantity();
    if (value > 0) {
      return value.toFixed(2);
    }
    return null;
  }

  function showRetailPrice() {
    return retailTotal() !== total();
  }

  function isMinQuantityInvalid() {
    if (sticker() || isPuff()) {
      return quantity() && quantity() < minStickerQuantity();
    }
    return false;
  }

  function showShippingProgress() {
    if ((isJerseyNumbers() || isLetters()) && quantity() && showFreeShipping) {
      return true;
    }
    return (
      !printAsIs() &&
      quantity() &&
      showFreeShipping &&
      artwork &&
      !artwork.isSimulated &&
      !artwork.isLoading
    );
  }

  function showBorderUI() {
    return showStepsUI() && !isMultiUpload;
  }

  useEffect(() => {
    updatePdpDates();
  }, [showShippingProgress()]);

  useEffect(
    () => () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    },
    []
  );

  function updateArtworkValues(...args) {
    return store.updateArtworkValues(...args);
  }

  function getArtworksPricing(...args) {
    return store.getArtworksPricing(...args);
  }

  function submitForm(...args) {
    return store.submitForm(...args);
  }

  function updatePdpDates(...args) {
    return store.updatePdpDates(...args);
  }

  function allowNumbersOnly(e) {
    const { value } = e.target;
    const quantityValue = value.replace(/[^0-9]/g, "").replace(/^0/, "");
    e.target.value = quantityValue;
    updateArtworkValues({
      index: artwork.originalIndex,
      quantity: quantityValue
    });
  }

  function handleInput(e) {
    allowNumbersOnly(e);
    debouncedHandleInputBlur();
  }

  function debouncedHandleInputBlur() {
    clearTimeout(debounceTimeout);
    setDebounceTimeout(
      setTimeout(() => {
        handleInputBlur();
      }, 1000)
    );
  }

  function handleInputBlur() {
    const currentData = {
      quantity: quantity()
    };
    if (isEqual(currentData, previousData)) {
      return;
    }
    setPreviousData(currentData);
    getArtworksPricing();
    updatePdpDates();
  }

  function formatCurrency(value) {
    if (value === null) return null;
    return intl.formatNumber(parseFloat(value), {
      style: "currency",
      currency: "USD"
    });
  }

  return (
    <div
      {...restProps}
      className={cn("artwork-quantity scope-45146366", restProps?.className, {
        "artwork-quantity--with-border": showBorderUI(),
        "artwork-quantity--with-border--invalid": showBorderUI() && isMinQuantityInvalid()
      })}
    >
      {!printAsIs() && !isJerseyNumbers() && !isLetters() && !showBorderUI() ? (
        <div className="artwork-quantity__title scope-45146366"> Quantity </div>
      ) : null}
      {showBorderUI() ? (
        <StepNumber
          className="scope-45146366"
          stepNumber={isPuff() ? 4 : 3}
          content="Add Quantity"
          data-testid="step-number"
        />
      ) : null}
      <div
        className={cn("artwork-quantity__row scope-45146366", {
          "artwork-quantity__row--gang": printAsIs(),
          "artwork-quantity__row--error": isMinQuantityInvalid()
        })}
      >
        {(isJerseyNumbers() || isLetters()) && quantity() ? (
          <div className="artwork-quantity__value scope-45146366">
            <div className="artwork-quantity__label scope-45146366">Quantity</div>
            <div className="artwork-quantity__jersey-numbers-total scope-45146366">
              {quantity()}
            </div>
          </div>
        ) : null}
        <div
          className={cn("artwork-quantity__prices scope-45146366", {
            hidden: printAsIs() || isJerseyNumbers() || isLetters()
          })}
        >
          {isJerseyNumbers() || isLetters() ? (
            <div className="artwork-quantity__label scope-45146366"> Unit Price </div>
          ) : null}
          <div className="artwork-quantity__retail-price scope-45146366">
            {formatCurrency(retailPrice())}
          </div>
          <div className="artwork-quantity__actual-price scope-45146366">
            {formatCurrency(unitPrice())}
          </div>
        </div>
        {!isJerseyNumbers() && !isLetters() ? (
          <div className="artwork-quantity__input-wrapper scope-45146366">
            {printAsIs() ? (
              <div className="artwork-quantity__title-wrapper scope-45146366">
                <label
                  className="artwork-quantity__title scope-45146366"
                  htmlFor={id()}
                  data-testid="artwork-quantity-title"
                >
                  Quantity
                </label>
              </div>
            ) : null}
            <input
              className={cn("scope-45146366", {
                "artwork-quantity__input js-quantity-form-input": true,
                "artwork-quantity__input--invalid": isMinQuantityInvalid()
              })}
              id={id()}
              maxLength="4"
              type="tel"
              name={id()}
              autoComplete="off"
              data-hj-allow={true}
              data-variant-id={variantId()}
              value={quantity()}
              disabled={disabled}
              onInput={handleInput}
              onBlur={handleInputBlur}
              onKeyUp={e => {
                if (e.key !== "Enter") return;
                submitForm(e);
              }}
              data-testid="artwork-quantity-input"
            />
            {outOfStock() ? (
              <UseSvg
                className="artwork-quantity__icon-cross scope-45146366"
                icon="cross"
                width="12"
                height="12"
                data-testid="artwork-quantity-icon-cross"
              />
            ) : null}
          </div>
        ) : null}
        {outOfStock() ? (
          <span className="artwork-quantity__out-of-stock-notice scope-45146366">Out of stock</span>
        ) : null}
        {printAsIs() ? (
          <div className="artwork-quantity__total-section scope-45146366">
            {quantity() > 0 && total() ? (
              <div className="artwork-quantity__total scope-45146366">
                <div className="artwork-quantity__total-wrapper scope-45146366">
                  {showRetailPrice() ? (
                    <div className="artwork-quantity__total-old scope-45146366">
                      {formatCurrency(retailTotal())}
                    </div>
                  ) : null}
                  <div className="artwork-quantity__total-price scope-45146366">
                    <span className="artwork-quantity__total-inline-title scope-45146366">
                      Total:
                    </span>{" "}
                    {formatCurrency(total())}
                  </div>
                </div>
              </div>
            ) : null}
            {quantity() > 0 && percentage() > 0 ? (
              <div className="artwork-quantity__off scope-45146366">{percentage()}% off </div>
            ) : null}
          </div>
        ) : null}
        {!printAsIs() && quantity() > 0 && total() ? (
          <div
            className={cn("scope-45146366", {
              "artwork-quantity__total": true,
              "artwork-quantity__total--mobile": isMobile()
            })}
          >
            <div
              className={cn("scope-45146366", {
                "artwork-quantity__total-title": !isJerseyNumbers() && !isLetters(),
                "artwork-quantity__label": isJerseyNumbers() || isLetters()
              })}
            >
              {" "}
              Total{" "}
            </div>
            <div className="artwork-quantity__total-wrapper scope-45146366">
              {showRetailPrice() ? (
                <div className="artwork-quantity__total-old scope-45146366">
                  {formatCurrency(retailTotal())}
                </div>
              ) : null}
              <div className="artwork-quantity__total-price scope-45146366">
                {formatCurrency(total())}
              </div>
            </div>
          </div>
        ) : null}
        {!printAsIs() && quantity() > 0 && percentage() > 0 ? (
          <div className="artwork-quantity__off scope-45146366">{percentage()}% off </div>
        ) : null}
        {isMinQuantityInvalid() ? (
          <ArtworkAlert
            className="artwork-quantity__error scope-45146366"
            data-testid="artwork-quantity-error"
          >
            {"Minimum quantity of"}
            {minStickerQuantity()}
            {"per design"}
          </ArtworkAlert>
        ) : null}
        {showShippingProgress() ? (
          <ArtworkShippingProgress
            className="scope-45146366"
            data-testid="artwork-shipping-progress"
          />
        ) : null}
      </div>
    </div>
  );
}
